import type { AnalysisMode, Platform } from '@rfz/contracts';
import { Badge, Button, Card, Input, cn } from '@rfz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  AlertTriangle,
  History,
  Loader2,
  PackageSearch,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  createResearchRun,
  controlJob,
  getResearchRun,
  getKeywordDetail,
  getSeasonalityEstimate,
  getWordstatVolumes,
  interpretProductIdea,
  listProductAnalyses,
  markWordstatOpened,
  refreshResearchCompetitors,
  refreshResearchSuggestions,
  refreshWordstatVolumes,
  selectResearchKeywords,
  type KeywordDetail,
  type ProductIdeaInterpretation,
  type ResearchProviderStatus,
  type ResearchRun,
  type ResearchSuggestion,
  type WordstatVolumeSnapshot,
  type WordstatVolumeStatus,
} from './api.js';
import { SeasonalityEstimatePanel } from './SeasonalityEstimatePanel.js';
import { SeoPromptLauncher } from './SeoPromptDialog.js';
import {
  buildPlatformSearchUrl,
  resolvePlatformVerificationKeyword,
  seasonalityAutoRecalculationKey,
  type SeasonalityWordstatObservation,
  type SeasonalityWordstatStatus,
} from './seasonality-ui.js';

type ExpressionDraft = ProductIdeaInterpretation['expressions'][number] & { selected: boolean };

type AnalysisDataType = '官方数据' | '卖家后台数据' | '估算数据' | '暂无数据';

interface AnalysisLocation {
  mode: AnalysisMode;
  taskId: string | null;
}

interface CompetitorResult {
  platform: Platform;
  keywordId: string;
  keywordRu: string;
  detail: KeywordDetail;
  status: 'ready' | 'queued' | 'failed';
}

const platformLabels: Record<Platform, string> = { wb: 'Wildberries' };
const analysisModeLabels: Record<AnalysisMode, string> = {
  standalone: '公开搜索分析',
  wb: 'WB 平台分析',
};
const expressionSourceLabels: Record<
  ProductIdeaInterpretation['expressions'][number]['source'],
  string
> = {
  dictionary: '词库',
  ai: 'DeepSeek',
  machine_translation: 'DLX 直译',
  user_input: '用户输入',
};
const decompositionMethodLabels: Record<
  NonNullable<ProductIdeaInterpretation['decomposition']>['method'],
  string
> = {
  rules: '标题规则拆解',
  dictionary: '标题拆解 + 词库',
  deepseek: '标题拆解 + DeepSeek 校正',
};
const providerLabels: Record<ResearchProviderStatus['provider'], string> = {
  yandex_suggest: 'Yandex',
  google_suggest: 'Google',
  wb_suggest: 'WB',
};
const sourceLabels: Record<ResearchSuggestion['sources'][number]['type'], string> = {
  local_dictionary: '词库',
  yandex_suggest: 'Yandex',
  google_suggest: 'Google',
  wb_suggest: 'WB',
  ai_supplement: 'AI',
};
const strengthLabels: Record<ResearchSuggestion['suggestStrength'], string> = {
  strong: '强信号',
  medium: '中等信号',
  weak: '弱信号',
  ai_only: '仅 AI 候选',
};

const wordstatStatusLabels: Record<WordstatVolumeStatus, string> = {
  pending: '待查询',
  queued: '排队中',
  running: '查询中',
  completed: '已完成',
  cache_hit: '使用缓存',
  failed: '查询失败',
  insufficient_balance: '余额不足',
};

function wordstatItem(snapshot: WordstatVolumeSnapshot | undefined, keyword: string) {
  const normalized = keyword
    .normalize('NFKC')
    .trim()
    .toLocaleLowerCase('ru-RU')
    .replace(/[\u201c\u201d\u00ab\u00bb"'`]+/gu, '')
    .replace(/\s+/gu, ' ');
  return snapshot?.items.find((item) => item.normalizedKeyword === normalized);
}

type WordstatVolumeItem = WordstatVolumeSnapshot['items'][number];

function KeywordResultItem({
  keyword,
  volumeItem,
  platform,
  primary,
  onSelectedChange,
  onOpenWordstat,
  onShowTrend,
}: {
  keyword: ResearchSuggestion;
  volumeItem: WordstatVolumeItem | undefined;
  platform: Platform | null;
  primary: boolean;
  onSelectedChange: (selected: boolean) => void;
  onOpenWordstat: () => void;
  onShowTrend: () => void;
}) {
  const wordstatVolume = volumeItem?.volumes.find((volume) => volume.type === 1);
  return (
    <article
      className={cn(
        'keyword-result-item',
        primary ? 'keyword-primary-card' : 'keyword-related-row',
      )}
      data-testid={primary ? 'primary-keyword-card' : 'related-keyword-row'}
    >
      <label className="keyword-select">
        <input
          type="checkbox"
          checked={keyword.selected}
          onChange={(event) => onSelectedChange(event.target.checked)}
          aria-label={`选择关键词 ${keyword.keywordRu}`}
        />
      </label>
      <div className="keyword-result-identity">
        {primary && <small>排序第一 · 完整关键词</small>}
        <strong>{keyword.keywordRu}</strong>
        <span>{keyword.translationZh ?? '暂无可靠中文翻译'}</span>
      </div>
      <div className="keyword-result-source">
        <span className="source-badges">
          {keyword.sources
            .filter(
              (source) => source.type !== 'wb_suggest' || source.type === `${platform}_suggest`,
            )
            .map((source) => (
              <em key={source.type}>{sourceLabels[source.type]}</em>
            ))}
        </span>
        <small>
          Yandex {sourceRank(keyword, 'yandex_suggest') ?? '—'} · Google{' '}
          {sourceRank(keyword, 'google_suggest') ?? '—'} · {strengthLabels[keyword.suggestStrength]}
        </small>
      </div>
      <div className="keyword-result-volume">
        <small>该关键词Wordstat频次</small>
        <strong>
          {wordstatVolume?.hasValue ? wordstatVolume.volume?.toLocaleString('zh-CN') : '—'}
        </strong>
        <span className="keyword-volume-state">
          <DataTypeBadge type={wordstatVolume?.hasValue ? '官方数据' : '暂无数据'} />
          {wordstatStatusLabels[volumeItem?.status ?? 'pending']}
        </span>
      </div>
      <div className="keyword-result-actions">
        {!primary && (
          <button type="button" onClick={onShowTrend}>
            查看趋势
          </button>
        )}
        {platform === 'wb' && (
          <a
            href={buildPlatformSearchUrl('wb', keyword.keywordRu)}
            target="_blank"
            rel="noopener noreferrer"
          >
            WB
          </a>
        )}
        <button type="button" onClick={onOpenWordstat}>
          Wordstat <ExternalLink size={12} />
        </button>
      </div>
    </article>
  );
}

function formatDate(value: string | null | undefined) {
  if (!value) return '刚刚';
  return new Intl.DateTimeFormat('zh-CN', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function sourceRank(
  keyword: ResearchSuggestion,
  type: ResearchSuggestion['sources'][number]['type'],
) {
  return keyword.sources.find((source) => source.type === type)?.rank ?? null;
}

function normalizedAnalysisMode(value: unknown): AnalysisMode {
  return value === 'wb' ? value : 'standalone';
}

function DataTypeBadge({ type }: { type: AnalysisDataType }) {
  return <span className="analysis-data-type">{type}</span>;
}

function median(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? (sorted[middle] ?? null)
    : ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}

function readAnalysisSeed(taskId: string | null) {
  try {
    const raw = sessionStorage.getItem('rfz:analysis-seed');
    if (!raw) return null;
    const value = JSON.parse(raw) as {
      productName?: unknown;
      sourceKeyword?: unknown;
      sourceKeywordId?: unknown;
      sourcePlatform?: unknown;
    };
    if (taskId && value.sourceKeywordId !== taskId) return null;
    return {
      productName: typeof value.productName === 'string' ? value.productName : '',
      sourceKeyword: typeof value.sourceKeyword === 'string' ? value.sourceKeyword : '',
    };
  } catch {
    return null;
  }
}

function recordText(value: unknown, fallback = '') {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
}

function actualCompetitors(detail: KeywordDetail) {
  const seen = new Set<string>();
  return detail.competitor.items.filter((item) => {
    if (item.isAd === true || (typeof item.relevance === 'number' && item.relevance < 0.5))
      return false;
    const source = typeof item.sourceStatus === 'string' ? item.sourceStatus : '';
    if (/mock|fixture|test/i.test(source)) return false;
    const key =
      recordText(item.variantKey) ||
      recordText(item.platformProductId) ||
      recordText(item.link) ||
      recordText(item.title);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function competitorFailureMessage(detail: KeywordDetail) {
  const summary = detail.competitor.latestAttempt?.errorSummary;
  if (!summary || typeof summary !== 'object') return '平台暂时限制自动读取';
  const message = (summary as Record<string, unknown>).message;
  if (typeof message !== 'string' || !message) return '平台暂时限制自动读取';
  if (/403|498|captcha|access/i.test(message)) return '平台暂时限制自动读取';
  return message;
}

async function waitForCompetitorDetail(platform: Platform, keywordId: string) {
  let detail = await getKeywordDetail(platform, keywordId);
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const status = detail.competitor.latestAttempt?.status;
    if (status === 'failed' || status === 'succeeded' || actualCompetitors(detail).length > 0)
      return detail;
    await new Promise((resolve) => setTimeout(resolve, 1_250));
    detail = await getKeywordDetail(platform, keywordId);
  }
  return detail;
}

function emptyKeywordDetail(): KeywordDetail {
  return {
    keyword: {} as never,
    dictionary: null,
    risk: null,
    score: null,
    competitor: {
      latestAttempt: null,
      latestSuccess: null,
      stale: false,
      items: [],
      sampleSet: null,
      sampleItems: [],
    },
    material: null,
    attributes: [],
    relatedKeywords: [],
    productCards: [],
  };
}

export function RecentAnalysesPanel({
  onClose,
  onOpenAnalysis,
}: {
  onClose: () => void;
  onOpenAnalysis: (location: AnalysisLocation) => void;
}) {
  const query = useQuery({ queryKey: ['product-analyses'], queryFn: listProductAnalyses });
  return (
    <div className="recent-analysis-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="recent-analysis-panel"
        role="dialog"
        aria-modal="true"
        aria-label="最近分析"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <p className="eyebrow">RECENT RESEARCH</p>
            <h2>最近分析</h2>
            <p>历史记录只在这里显示，不进入选品分析首屏。</p>
          </div>
          <Button variant="ghost" size="icon" aria-label="关闭最近分析" onClick={onClose}>
            <X size={18} />
          </Button>
        </header>
        {query.isLoading ? (
          <div className="table-state">
            <Loader2 className="spin" /> 正在读取…
          </div>
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <div className="recent-analysis-empty">
            <History size={24} />
            <strong>还没有分析记录</strong>
          </div>
        ) : (
          <div className="recent-analysis-list">
            {query.data?.items.map((detail) => (
              <article key={detail.analysis.id}>
                <div>
                  <strong>{detail.product.canonicalName}</strong>
                  <span>
                    {analysisModeLabels[normalizedAnalysisMode(detail.analysis.analysisMode)]}
                  </span>
                </div>
                <div>
                  <Badge>{detail.analysis.status === 'complete' ? '已完成' : '研究中'}</Badge>
                  <time>{formatDate(detail.currentVersion?.createdAt)}</time>
                  {detail.analysis.status === 'research' && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        onOpenAnalysis({
                          mode: normalizedAnalysisMode(detail.analysis.analysisMode),
                          taskId: detail.analysis.id,
                        })
                      }
                    >
                      继续分析
                    </Button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}

export function ProductResearchFlow({
  mode,
  taskId,
  onLocationChange,
}: AnalysisLocation & { onLocationChange: (location: AnalysisLocation) => void }) {
  const seed = readAnalysisSeed(taskId);
  const [idea, setIdea] = useState(seed?.productName || seed?.sourceKeyword || '');
  const [interpretation, setInterpretation] = useState<ProductIdeaInterpretation | null>(null);
  const [expressions, setExpressions] = useState<ExpressionDraft[]>([]);
  const [run, setRun] = useState<ResearchRun | null>(null);
  const [competitors, setCompetitors] = useState<CompetitorResult[]>([]);
  const [visibleLimit, setVisibleLimit] = useState(10);
  const [trendKeywordOverride, setTrendKeywordOverride] = useState<string | null>(null);
  const [seasonalityAutoRefreshNotice, setSeasonalityAutoRefreshNotice] = useState<string | null>(
    null,
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const activeTaskId = useRef<string | null>(seed ? taskId : null);
  const activeMode = useRef<AnalysisMode>(mode);
  const trendPanelRef = useRef<HTMLDivElement>(null);
  const previousSeasonalityObservations = useRef(new Map<string, SeasonalityWordstatObservation>());
  const completedSeasonalityAutoRefreshes = useRef(new Set<string>());
  const requestedWordstatBackfills = useRef(new Set<string>());
  const seasonalityNoticeTimer = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const wordstatQuery = useQuery({
    queryKey: ['wordstat-volumes', run?.analysis.id],
    queryFn: () => getWordstatVolumes(run!.analysis.id),
    enabled: Boolean(run?.analysis.id),
    refetchInterval: 1_500,
  });
  const forceWordstatMutation = useMutation({
    mutationFn: () => refreshWordstatVolumes(run!.analysis.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['wordstat-volumes', run?.analysis.id] }),
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
      ]);
    },
  });
  const retryWordstatMutation = useMutation({
    mutationFn: () => controlJob(wordstatQuery.data!.job!.id, 'retry'),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['wordstat-volumes', run?.analysis.id] }),
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
      ]);
    },
  });

  useEffect(() => {
    const analysisId = run?.analysis.id;
    const snapshot = wordstatQuery.data;
    if (!analysisId || !snapshot?.configured || requestedWordstatBackfills.current.has(analysisId))
      return;
    const active = snapshot.job?.status === 'queued' || snapshot.job?.status === 'running';
    const hasPending = snapshot.items.some((item) => item.status === 'pending');
    if (active || !hasPending) return;
    requestedWordstatBackfills.current.add(analysisId);
    void refreshWordstatVolumes(analysisId, false)
      .then(() =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ['wordstat-volumes', analysisId] }),
          queryClient.invalidateQueries({ queryKey: ['jobs'] }),
        ]),
      )
      .catch(() => requestedWordstatBackfills.current.delete(analysisId));
  }, [queryClient, run?.analysis.id, wordstatQuery.data]);

  const platform: Platform | null = mode === 'standalone' ? null : mode;

  const selected = run?.suggestions.filter((item) => item.selected) ?? [];
  const visible = [...(run?.suggestions ?? [])].sort((left, right) => {
    const leftVolume = wordstatItem(wordstatQuery.data, left.keywordRu)?.volumes.find(
      (volume) => volume.type === 1,
    )?.volume;
    const rightVolume = wordstatItem(wordstatQuery.data, right.keywordRu)?.volumes.find(
      (volume) => volume.type === 1,
    )?.volume;
    return (
      Number(right.selected) - Number(left.selected) ||
      (rightVolume ?? -1) - (leftVolume ?? -1) ||
      right.relevance - left.relevance
    );
  });
  const primaryKeyword = visible[0] ?? null;
  const seoPromptKeywords = visible.slice(0, 50).map((keyword) => {
    const volume = wordstatItem(wordstatQuery.data, keyword.keywordRu)?.volumes.find(
      (item) => item.type === 1,
    );
    return {
      keyword: keyword.keywordRu,
      ...(volume?.hasValue && typeof volume.volume === 'number' ? { heat: volume.volume } : {}),
      relevance: keyword.relevance,
      sourceCount: keyword.sourceCount,
    };
  });
  const platformVerificationKeyword = resolvePlatformVerificationKeyword(
    visible,
    run?.state.coreQueries[0]?.keywordRu ?? primaryKeyword?.keywordRu ?? '',
  );
  const activeKeyword =
    (trendKeywordOverride && visible.some((keyword) => keyword.keywordRu === trendKeywordOverride)
      ? trendKeywordOverride
      : primaryKeyword?.keywordRu) ??
    run?.state.coreQueries[0]?.keywordRu ??
    '';
  const activeWordstatItem = wordstatItem(wordstatQuery.data, activeKeyword);
  const activeVolumeResult = activeWordstatItem?.volumes.find((volume) => volume.type === 1);
  const trendWordstatVolume = activeVolumeResult?.hasValue ? activeVolumeResult.volume : null;
  const activeWordstatStatus: SeasonalityWordstatStatus =
    wordstatQuery.data?.job?.status === 'queued' || wordstatQuery.data?.job?.status === 'running'
      ? wordstatQuery.data.job.status
      : (activeVolumeResult?.status ?? activeWordstatItem?.status ?? 'pending');
  const seasonalityQuery = useQuery({
    queryKey: [
      'seasonality-estimate',
      run?.analysis.id,
      activeKeyword,
      trendWordstatVolume ?? 'missing',
    ],
    queryFn: () => getSeasonalityEstimate(run!.analysis.id, activeKeyword),
    enabled: Boolean(run?.analysis.id && activeKeyword),
    retry: false,
  });
  const refreshSeasonalityMutation = useMutation({
    mutationFn: () => getSeasonalityEstimate(run!.analysis.id, activeKeyword, true),
    onSuccess: (estimate) => {
      queryClient.setQueryData(
        ['seasonality-estimate', run?.analysis.id, activeKeyword, trendWordstatVolume ?? 'missing'],
        estimate,
      );
    },
  });

  useEffect(() => {
    if (!run?.analysis.id || !activeKeyword || !wordstatQuery.data) return;
    const region = wordstatQuery.data.region?.regionKey ?? run.config.wordstatRegion;
    const scope = `${run.analysis.id}|${activeKeyword.trim().toLocaleLowerCase('ru-RU')}|${region}`;
    const current: SeasonalityWordstatObservation = {
      keyword: activeKeyword,
      region,
      status: activeWordstatStatus,
      phraseVolume: trendWordstatVolume,
    };
    const previous = previousSeasonalityObservations.current.get(scope);
    previousSeasonalityObservations.current.set(scope, current);
    const resultKey = seasonalityAutoRecalculationKey(previous, current);
    if (!resultKey || completedSeasonalityAutoRefreshes.current.has(resultKey)) return;
    completedSeasonalityAutoRefreshes.current.add(resultKey);
    const queryKey = [
      'seasonality-estimate',
      run.analysis.id,
      activeKeyword,
      trendWordstatVolume ?? 'missing',
    ] as const;
    void queryClient
      .cancelQueries({ queryKey, exact: true })
      .then(() =>
        queryClient.fetchQuery({
          queryKey,
          queryFn: () => getSeasonalityEstimate(run.analysis.id, activeKeyword, true),
          staleTime: 0,
        }),
      )
      .then(() => {
        setSeasonalityAutoRefreshNotice('已基于最新Wordstat频次重新计算');
        if (seasonalityNoticeTimer.current != null)
          window.clearTimeout(seasonalityNoticeTimer.current);
        seasonalityNoticeTimer.current = window.setTimeout(
          () => setSeasonalityAutoRefreshNotice(null),
          6_000,
        );
      })
      .catch(() => undefined);
  }, [
    activeKeyword,
    activeWordstatStatus,
    queryClient,
    run,
    trendWordstatVolume,
    wordstatQuery.data,
  ]);

  useEffect(
    () => () => {
      if (seasonalityNoticeTimer.current != null)
        window.clearTimeout(seasonalityNoticeTimer.current);
    },
    [],
  );
  const latestWordstatUpdate = wordstatQuery.data?.items
    .flatMap((item) => item.volumes.map((volume) => volume.queriedAt).filter(Boolean))
    .sort()
    .at(-1);
  const visibleProviderStatuses = (run?.state.providerStatuses ?? []).filter((status) => {
    if (status.provider === 'yandex_suggest' || status.provider === 'google_suggest') return true;
    return platform ? status.provider === `${platform}_suggest` : false;
  });
  const platformResults = platform
    ? competitors.filter((result) => result.platform === platform)
    : [];
  const platformItems = platformResults.flatMap((result) => actualCompetitors(result.detail));
  const platformPrices = platformItems
    .map((item) => item.price)
    .filter((value): value is number => typeof value === 'number');
  const platformReviews = platformItems
    .map((item) => item.reviewCount)
    .filter((value): value is number => typeof value === 'number');
  const platformCategories = [
    ...new Set(
      platformItems
        .map((item) => recordText(item.category))
        .filter((value): value is string => Boolean(value)),
    ),
  ];
  const platformProvider = visibleProviderStatuses.find(
    (status) => status.provider === `${platform}_suggest`,
  );
  const platformDemandIndex =
    platformProvider?.status === 'ready' || platformProvider?.status === 'stale'
      ? Math.min(
          100,
          platformProvider.itemCount * 8 +
            visible.filter((keyword) =>
              keyword.sources.some((source) => source.type === `${platform}_suggest`),
            ).length *
              4,
        )
      : null;
  const platformCompetitionIndex = platformItems.length
    ? Math.min(100, platformItems.length * 10 + (median(platformReviews) ?? 0) / 100)
    : null;
  const searchIntent = run?.state.useCases.length
    ? `围绕${run.state.useCases.slice(0, 2).join('、')}的商品型搜索`
    : '商品发现与购买比较意图';
  const commonConclusion =
    wordstatQuery.data?.job?.status === 'failed'
      ? 'Yandex Wordstat 结果读取中断，现有 Topvisor 任务可继续读取，不应据此判断需求为 0。'
      : (wordstatQuery.data?.completeness ?? 0) > 0
        ? '已有 Yandex Wordstat 俄罗斯地区频次数据，可结合趋势继续验证。'
        : visible.some((keyword) => keyword.verifiedBySuggest)
          ? '已发现公开搜索相关词，但绝对需求仍需通过 Yandex Wordstat 核验。'
          : '公开搜索信号不足，暂不建议据此做采购决策。';

  useEffect(() => {
    if (activeMode.current !== mode && activeTaskId.current !== taskId) {
      const nextSeed = readAnalysisSeed(taskId);
      setIdea(nextSeed?.productName || nextSeed?.sourceKeyword || '');
      setInterpretation(null);
      setExpressions([]);
      setRun(null);
      setCompetitors([]);
      setVisibleLimit(10);
      setTrendKeywordOverride(null);
      activeMode.current = mode;
      activeTaskId.current = nextSeed ? taskId : null;
    }
    if (!taskId || activeTaskId.current === taskId) return;
    let active = true;
    activeTaskId.current = taskId;
    setHistoryLoading(true);
    void getResearchRun(taskId)
      .then((savedRun) => {
        if (!active) return;
        const savedMode = normalizedAnalysisMode(savedRun.analysis.analysisMode);
        const confidence = Math.max(
          0,
          ...savedRun.state.coreQueries.map((query) => query.confidence),
        );
        setIdea(savedRun.state.idea);
        setInterpretation({
          inputLanguage: /[\u0400-\u04ff]/u.test(savedRun.state.idea) ? 'ru' : 'zh',
          entity: savedRun.state.entityZh,
          productType: savedRun.state.productTypeZh,
          useCases: savedRun.state.useCases,
          confidence,
          ambiguities: [],
          expressions: savedRun.state.coreQueries.map((query) => ({
            russian: query.keywordRu,
            chineseBackTranslation: query.translationZh,
            confidence: query.confidence,
            source: 'user_input',
          })),
        });
        setExpressions(
          savedRun.state.coreQueries.map((query) => ({
            russian: query.keywordRu,
            chineseBackTranslation: query.translationZh,
            confidence: query.confidence,
            source: 'user_input',
            selected: true,
          })),
        );
        setRun(savedRun);
        setCompetitors([]);
        setTrendKeywordOverride(null);
        activeMode.current = savedMode;
        if (savedMode !== mode) onLocationChange({ mode: savedMode, taskId });
      })
      .catch(async () => {
        // A platform task id may seed a new analysis before a research run exists.
        if (!active || mode === 'standalone') return;
        try {
          const detail = await getKeywordDetail(mode, taskId);
          if (!active) return;
          setIdea(
            detail.keyword.standardProductName ??
              detail.keyword.chineseTranslation ??
              detail.keyword.originalKeyword,
          );
        } catch {
          // Keep the new-analysis form usable when the source task no longer exists.
        }
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => {
      active = false;
    };
    // The URL mode/task pair is the trigger; the callback is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, taskId]);

  const startMutation = useMutation({
    mutationFn: async () => {
      const interpreted = await interpretProductIdea({ idea: idea.trim(), analysisMode: mode });
      const nextExpressions = (
        interpreted.expressions.length
          ? interpreted.expressions
          : [
              {
                russian: '',
                chineseBackTranslation: null,
                confidence: 0,
                source: 'user_input' as const,
              },
            ]
      ).map((item) => ({ ...item, selected: true }));
      const coreQueries = nextExpressions
        .filter((item) => item.russian.trim())
        .slice(0, 3)
        .map((item) => ({
          keywordRu: item.russian.trim().slice(0, 200),
          translationZh: item.chineseBackTranslation?.trim().slice(0, 200) || null,
          confidence: item.confidence,
        }));
      if (!coreQueries.length)
        throw new Error('未生成可靠俄语核心词，请检查 DLX 或 DeepSeek，或手动填写俄语词');
      const created = await createResearchRun({
        idea: idea.trim(),
        entityZh: (interpreted.entity.trim() || idea.trim()).slice(0, 200),
        productTypeZh: interpreted.productType?.trim().slice(0, 120) || null,
        useCases: interpreted.useCases
          .map((item) => item.trim().slice(0, 120))
          .filter(Boolean)
          .slice(0, 5),
        coreQueries,
        analysisMode: mode,
      });
      const refreshed = await refreshResearchSuggestions(created.analysis.id, false);
      return { interpreted, expressions: nextExpressions, run: refreshed };
    },
    onSuccess: (result) => {
      setInterpretation(result.interpreted);
      setExpressions(result.expressions);
      setRun(result.run);
      setCompetitors([]);
      setVisibleLimit(10);
      setTrendKeywordOverride(null);
      activeTaskId.current = result.run.analysis.id;
      activeMode.current = mode;
      onLocationChange({ mode, taskId: result.run.analysis.id });
    },
  });

  const editedSearchMutation = useMutation({
    mutationFn: async () => {
      if (!interpretation) throw new Error('请先输入商品想法');
      const coreQueries = expressions
        .filter((item) => item.selected && item.russian.trim())
        .slice(0, 3)
        .map((item) => ({
          keywordRu: item.russian.trim().slice(0, 200),
          translationZh: item.chineseBackTranslation?.trim().slice(0, 200) || null,
          confidence: item.confidence,
        }));
      if (!coreQueries.length) throw new Error('请至少保留一个俄语核心词');
      const created = await createResearchRun({
        idea: idea.trim(),
        entityZh: (interpretation.entity.trim() || idea.trim()).slice(0, 200),
        productTypeZh: interpretation.productType?.trim().slice(0, 120) || null,
        useCases: interpretation.useCases
          .map((item) => item.trim().slice(0, 120))
          .filter(Boolean)
          .slice(0, 5),
        coreQueries,
        analysisMode: mode,
      });
      return refreshResearchSuggestions(created.analysis.id, false);
    },
    onSuccess: (result) => {
      setRun(result);
      setCompetitors([]);
      setVisibleLimit(10);
      setTrendKeywordOverride(null);
      activeTaskId.current = result.analysis.id;
      activeMode.current = mode;
      onLocationChange({ mode, taskId: result.analysis.id });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshResearchSuggestions(run!.analysis.id, true),
    onSuccess: (result) => {
      setRun(result);
      setCompetitors([]);
    },
  });

  const openWordstat = async (keyword: ResearchSuggestion) => {
    const link = document.createElement('a');
    link.href = keyword.wordstatUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
    if (!run) return;
    try {
      await markWordstatOpened(run.analysis.id, keyword.id);
    } catch {
      // The external link must continue working if optional tracking fails.
    }
  };

  const competitorMutation = useMutation({
    mutationFn: async (requestedPlatform: Platform) => {
      const saved = await selectResearchKeywords(
        run!.analysis.id,
        selected.slice(0, 3).map((item) => item.id),
      );
      setRun(saved);
      const refresh = await refreshResearchCompetitors(saved.analysis.id, [requestedPlatform]);
      return Promise.all(
        refresh.items.map(async (target): Promise<CompetitorResult> => {
          try {
            const detail = await waitForCompetitorDetail(target.platform, target.keywordId);
            const ready = actualCompetitors(detail).length > 0 || target.cacheHit;
            return {
              platform: target.platform,
              keywordId: target.keywordId,
              keywordRu: target.keywordRu,
              detail,
              status:
                ready || detail.competitor.latestAttempt?.status === 'succeeded'
                  ? 'ready'
                  : 'failed',
            };
          } catch {
            return {
              platform: target.platform,
              keywordId: target.keywordId,
              keywordRu: target.keywordRu,
              detail: emptyKeywordDetail(),
              status: 'failed',
            };
          }
        }),
      );
    },
    onSuccess: setCompetitors,
  });

  const resetResearch = () => {
    setInterpretation(null);
    setExpressions([]);
    setRun(null);
    setCompetitors([]);
    setVisibleLimit(10);
    setTrendKeywordOverride(null);
    activeTaskId.current = null;
    activeMode.current = mode;
    onLocationChange({ mode, taskId: null });
  };

  if (historyLoading)
    return (
      <section className="product-research-flow research-home-view compact-research-home">
        <div className="table-state">
          <Loader2 className="spin" /> 正在恢复历史分析…
        </div>
      </section>
    );

  if (!run || !interpretation)
    return (
      <section className="product-research-flow research-home-view compact-research-home">
        <header className="research-home-heading">
          <p className="eyebrow">PRODUCT DISCOVERY</p>
          <h2>选品分析</h2>
          <p>{analysisModeLabels[mode]} · 输入商品想法，获得俄语关键词与对应数据结论。</p>
        </header>
        <Card className="research-idea-card compact-idea-card">
          <label htmlFor="research-idea">今天想研究什么商品？</label>
          <textarea
            id="research-idea"
            aria-label="商品想法"
            value={idea}
            maxLength={2_000}
            onChange={(event) => setIdea(event.target.value)}
            placeholder="例如：可折叠的露营桌，重量轻，适合汽车出行"
          />
          <div className="research-input-hint">
            <span>支持中文商品名、1688 长标题、中文商品想法或俄语关键词</span>
            <span>{idea.length}/2000</span>
          </div>
          <div className="compact-search-controls">
            <div className="analysis-fixed-mode">
              <small>当前模式</small>
              <strong>{analysisModeLabels[mode]}</strong>
            </div>
            <Button
              className="research-primary-action"
              disabled={!idea.trim() || startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              {startMutation.isPending ? (
                <Loader2 className="spin" size={17} />
              ) : (
                <Search size={17} />
              )}
              {startMutation.isPending ? '正在搜索并整理…' : '开始分析'}
            </Button>
          </div>
          {startMutation.isError && <p className="form-error">{startMutation.error.message}</p>}
        </Card>
      </section>
    );

  return (
    <section className="product-research-flow compact-research-report">
      <header className="compact-report-header">
        <div>
          <p className="eyebrow">PRODUCT DISCOVERY</p>
          <h2>{interpretation.entity || '选品分析'}</h2>
          <p>
            {analysisModeLabels[mode]} · {idea}
          </p>
        </div>
        <div className="compact-report-header-actions">
          <SeoPromptLauncher
            platform={platform}
            keywords={seoPromptKeywords}
            initialProductText={idea}
          />
          <Button variant="ghost" onClick={resetResearch}>
            <RefreshCw size={15} />
            分析新商品
          </Button>
        </div>
      </header>

      <Card className="compact-query-card">
        <div className="compact-query-summary">
          <div>
            <small>识别结果</small>
            <strong>{interpretation.productType ?? interpretation.entity}</strong>
            <span>{Math.round(interpretation.confidence * 100)}% 置信度</span>
          </div>
          <div className="compact-expression-list">
            {expressions.map((expression, index) => (
              <label key={`${index}:${expression.russian}`}>
                <input
                  type="checkbox"
                  checked={expression.selected}
                  onChange={(event) =>
                    setExpressions((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, selected: event.target.checked } : item,
                      ),
                    )
                  }
                />
                <span>
                  <Input
                    aria-label={`俄语核心表达 ${index + 1}`}
                    value={expression.russian}
                    onChange={(event) =>
                      setExpressions((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, russian: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  <small>
                    {expression.chineseBackTranslation ?? '暂无中文回译'} ·{' '}
                    {expressionSourceLabels[expression.source]}
                  </small>
                </span>
              </label>
            ))}
          </div>
          <Button
            variant="ghost"
            disabled={editedSearchMutation.isPending}
            onClick={() => editedSearchMutation.mutate()}
          >
            {editedSearchMutation.isPending ? (
              <Loader2 className="spin" size={15} />
            ) : (
              <Search size={15} />
            )}
            用修改后的词重搜
          </Button>
        </div>
        {interpretation.decomposition && (
          <div className="title-decomposition" aria-label="1688 标题拆解结果">
            <div className="title-decomposition-heading">
              <div>
                <small>主商品</small>
                <strong>{interpretation.decomposition.primaryProduct}</strong>
              </div>
              <span>{decompositionMethodLabels[interpretation.decomposition.method]}</span>
            </div>
            {[
              ['相关商品', interpretation.decomposition.secondaryProducts],
              ['属性', interpretation.decomposition.attributes],
              ['使用场景', interpretation.decomposition.useCases],
              ['已排除', interpretation.decomposition.excludedTerms],
            ].map(
              ([label, values]) =>
                (values as string[]).length > 0 && (
                  <div className="title-decomposition-row" key={label as string}>
                    <small>{label as string}</small>
                    <div>
                      {(values as string[]).map((value) => (
                        <span key={value}>{value}</span>
                      ))}
                    </div>
                  </div>
                ),
            )}
          </div>
        )}
        {interpretation.ambiguities.length > 0 && (
          <p className="compact-ambiguity">{interpretation.ambiguities.join('；')}</p>
        )}
      </Card>

      <div className="keyword-results-stack">
        <Card className="keyword-overview-card" data-testid="keyword-overview">
          <header className="compact-section-header">
            <div>
              <h3>搜索关键词与中文翻译</h3>
              <p>每个词展示Wordstat搜索框无运算符时的俄区近30天通用频次。</p>
            </div>
            <Button
              variant="ghost"
              disabled={!wordstatQuery.data?.configured || forceWordstatMutation.isPending}
              onClick={() => forceWordstatMutation.mutate()}
              title="忽略30天频次缓存并重新计算季节性，可能产生新的Topvisor费用"
            >
              <RefreshCw
                size={14}
                className={forceWordstatMutation.isPending ? 'spin' : undefined}
              />
              强制刷新
            </Button>
          </header>
          <div className="keyword-provider-health" aria-label="关联词来源状态">
            <div>
              <small>关联词来源</small>
              {visibleProviderStatuses.map((status) => (
                <span
                  className={cn('keyword-provider-chip', `status-${status.status}`)}
                  key={status.provider}
                >
                  <i />
                  {providerLabels[status.provider]}
                  <strong>
                    {status.status === 'ready'
                      ? status.itemCount
                      : status.status === 'stale'
                        ? `${status.itemCount}缓存`
                        : status.status === 'blocked'
                          ? '受限'
                          : '不可用'}
                  </strong>
                </span>
              ))}
            </div>
            <button
              type="button"
              disabled={refreshMutation.isPending}
              onClick={() => refreshMutation.mutate()}
            >
              {refreshMutation.isPending ? (
                <Loader2 className="spin" size={13} />
              ) : (
                <RefreshCw size={13} />
              )}
              刷新关联词
            </button>
          </div>
          <div className="keyword-overview-summary" aria-label="Yandex Wordstat 数据摘要">
            <span>数据来源：{wordstatQuery.data?.source ?? 'Topvisor / Yandex Wordstat'}</span>
            <span>地区：{wordstatQuery.data?.region?.name ?? '俄罗斯（待识别）'}</span>
            <span>数据完整度：{Math.round((wordstatQuery.data?.completeness ?? 0) * 100)}%</span>
            <span>
              {wordstatQuery.data?.job
                ? wordstatQuery.data.job.status === 'running'
                  ? '查询中'
                  : wordstatQuery.data.job.status === 'queued'
                    ? '排队中'
                    : wordstatQuery.data.job.status === 'failed'
                      ? '查询失败'
                      : wordstatQuery.data.job.status === 'succeeded'
                        ? '已完成'
                        : '已停止'
                : wordstatQuery.data?.configured
                  ? '待查询'
                  : '暂无数据'}
            </span>
            <span>最后更新：{latestWordstatUpdate ? formatDate(latestWordstatUpdate) : '—'}</span>
          </div>
          {wordstatQuery.data?.job?.status === 'failed' && (
            <div className="wordstat-state-banner wordstat-state-error" role="alert">
              <AlertTriangle size={19} />
              <div>
                <strong>任务已创建，但结果读取中断</strong>
                <span>可以继续读取现有 Topvisor 任务，不会重复创建任务或再次扣费。</span>
              </div>
              <Button
                variant="secondary"
                disabled={retryWordstatMutation.isPending}
                onClick={() => retryWordstatMutation.mutate()}
              >
                <RefreshCw
                  size={15}
                  className={retryWordstatMutation.isPending ? 'spin' : undefined}
                />
                继续读取结果
              </Button>
            </div>
          )}
          <details className="keyword-overview-note">
            <summary>
              <ShieldCheck size={15} /> 数据口径说明
            </summary>
            <p>
              Yandex俄区近30天搜索频次由Topvisor查询Yandex
              Wordstat；查询使用无运算符的完整关键词，口径与直接把该词粘贴到Wordstat“热门查询”搜索框一致，会统计包含该关键词及其词形的查询。空值、失败和余额不足不会显示为0，Wordstat返回0时会保留为真实结果，“—”表示尚未取得数据。
            </p>
          </details>
        </Card>

        <div ref={trendPanelRef} className="seasonality-panel-anchor">
          <SeasonalityEstimatePanel
            keyword={activeKeyword}
            estimate={seasonalityQuery.data}
            loading={seasonalityQuery.isLoading}
            refreshing={refreshSeasonalityMutation.isPending}
            autoRefreshNotice={seasonalityAutoRefreshNotice}
            onRefresh={() => refreshSeasonalityMutation.mutate()}
          />
        </div>
        {primaryKeyword && (
          <section className="primary-keyword-section" aria-labelledby="primary-keyword-heading">
            <header className="result-section-heading">
              <div>
                <p className="eyebrow">PRIMARY KEYWORD</p>
                <h3 id="primary-keyword-heading">第一个关键词</h3>
              </div>
              <span>默认展示排序第一的完整信息</span>
            </header>
            <KeywordResultItem
              keyword={primaryKeyword}
              volumeItem={wordstatItem(wordstatQuery.data, primaryKeyword.keywordRu)}
              platform={platform}
              primary
              onSelectedChange={(checked) =>
                setRun((current) =>
                  current
                    ? {
                        ...current,
                        suggestions: current.suggestions.map((item) =>
                          item.id === primaryKeyword.id ? { ...item, selected: checked } : item,
                        ),
                      }
                    : current,
                )
              }
              onOpenWordstat={() => void openWordstat(primaryKeyword)}
              onShowTrend={() => undefined}
            />
          </section>
        )}
        <Card className="related-keywords-panel" data-testid="related-keywords-panel">
          <header className="result-section-heading">
            <div>
              <p className="eyebrow">RELATED KEYWORDS</p>
              <h3>相关关键词</h3>
            </div>
            <span>
              已选 {selected.length} 个 · 共 {visible.length} 个相关词
            </span>
          </header>
          <div className="related-keyword-list">
            {visible.slice(1, visibleLimit).map((keyword) => (
              <KeywordResultItem
                key={keyword.id}
                keyword={keyword}
                volumeItem={wordstatItem(wordstatQuery.data, keyword.keywordRu)}
                platform={platform}
                primary={false}
                onSelectedChange={(checked) =>
                  setRun((current) =>
                    current
                      ? {
                          ...current,
                          suggestions: current.suggestions.map((item) =>
                            item.id === keyword.id ? { ...item, selected: checked } : item,
                          ),
                        }
                      : current,
                  )
                }
                onOpenWordstat={() => void openWordstat(keyword)}
                onShowTrend={() => {
                  setTrendKeywordOverride(keyword.keywordRu);
                  window.setTimeout(
                    () =>
                      trendPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                    0,
                  );
                }}
              />
            ))}
          </div>
          {visible.length <= 1 && <p className="related-keyword-empty">暂无其他相关关键词。</p>}
          {visibleLimit < visible.length && (
            <footer className="compact-keyword-footer">
              <span>已显示 {Math.max(0, visibleLimit - 1)} 个相关词</span>
              <Button variant="ghost" onClick={() => setVisibleLimit((current) => current + 10)}>
                展开更多
              </Button>
            </footer>
          )}
        </Card>
        <Card className="analysis-summary-panel" data-testid="analysis-summary-panel">
          <header className="result-section-heading">
            <div>
              <p className="eyebrow">GENERAL ANALYSIS</p>
              <h3>通用分析摘要</h3>
            </div>
            <DataTypeBadge type="估算数据" />
          </header>
          <div className="analysis-summary-grid">
            <div>
              <small>搜索意图</small>
              <strong>{searchIntent}</strong>
            </div>
            <div>
              <small>关键词相关性</small>
              <strong>
                {visible.length
                  ? `${Math.round(
                      (visible.reduce((sum, keyword) => sum + keyword.relevance, 0) /
                        visible.length) *
                        100,
                    )}%`
                  : '暂无数据'}
              </strong>
            </div>
            <div>
              <small>通用风险</small>
              <strong>
                {visible.some((keyword) => !keyword.verifiedBySuggest)
                  ? '部分关键词缺少公开来源交叉验证'
                  : '未发现通用高风险信号'}
              </strong>
            </div>
          </div>
          <div className="analysis-summary-conclusion">
            <small>综合判断</small>
            <strong>{commonConclusion}</strong>
          </div>
        </Card>
      </div>

      {platform && (
        <>
          <section className="analysis-platform-section" data-platform={platform}>
            <header className="compact-section-header">
              <div>
                <h3>WB平台数据</h3>
                <p>平台公开信号和竞品样本均按数据类型标注，不等同于官方查询次数。</p>
              </div>
            </header>
            <div className="analysis-platform-metrics">
              <Card>
                <small>WB平台需求</small>
                <strong>
                  {platformDemandIndex == null ? '暂无数据' : `需求指数 ${platformDemandIndex}`}
                </strong>
                <DataTypeBadge type={platformDemandIndex == null ? '暂无数据' : '估算数据'} />
              </Card>
              <Card>
                <small>WB竞争度</small>
                <strong>
                  {platformCompetitionIndex == null
                    ? '暂无数据'
                    : `竞争指数 ${Math.round(platformCompetitionIndex)}`}
                </strong>
                <DataTypeBadge type={platformCompetitionIndex == null ? '暂无数据' : '估算数据'} />
              </Card>
              <Card>
                <small>WB搜索结果</small>
                <strong>
                  {platformItems.length ? `${platformItems.length} 个有效样本` : '暂无数据'}
                </strong>
                <DataTypeBadge type={platformItems.length ? '估算数据' : '暂无数据'} />
              </Card>
              <Card>
                <small>WB价格分布</small>
                <strong>
                  {platformPrices.length
                    ? `${Math.min(...platformPrices).toLocaleString()}–${Math.max(
                        ...platformPrices,
                      ).toLocaleString()} ₽`
                    : '暂无数据'}
                </strong>
                <DataTypeBadge type={platformPrices.length ? '估算数据' : '暂无数据'} />
              </Card>
              <Card>
                <small>WB评价数据</small>
                <strong>
                  {platformReviews.length
                    ? `评价中位数 ${Math.round(median(platformReviews) ?? 0).toLocaleString()}`
                    : '暂无数据'}
                </strong>
                <DataTypeBadge type={platformReviews.length ? '估算数据' : '暂无数据'} />
              </Card>
              <Card>
                <small>WB类目数据</small>
                <strong>{platformCategories.slice(0, 2).join('、') || '暂无数据'}</strong>
                <DataTypeBadge type={platformCategories.length ? '估算数据' : '暂无数据'} />
              </Card>
              <Card className="analysis-platform-conclusion">
                <small>WB平台结论</small>
                <strong>
                  {platformDemandIndex == null && platformCompetitionIndex == null
                    ? '平台数据不足，暂不输出强结论。'
                    : platformDemandIndex != null &&
                        platformDemandIndex >= 50 &&
                        (platformCompetitionIndex ?? 0) < 70
                      ? '平台需求信号较积极，可进入小批量验证。'
                      : '平台信号有限或竞争偏高，建议谨慎验证。'}
                </strong>
                <DataTypeBadge
                  type={
                    platformDemandIndex == null && platformCompetitionIndex == null
                      ? '暂无数据'
                      : '估算数据'
                  }
                />
              </Card>
            </div>
          </section>

          <Card className="compact-competitor-panel" data-platform={platform}>
            <header className="compact-section-header competitor-heading-row">
              <div>
                <h3>WB竞品</h3>
                <p>平台限制自动采集时仍可直接打开真实搜索结果，不展示模拟数据。</p>
              </div>
              <div className="competitor-direct-actions">
                {activeKeyword && (
                  <a
                    href={buildPlatformSearchUrl(platform, activeKeyword)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    打开 WB 搜索 <ExternalLink size={13} />
                  </a>
                )}
                <Button
                  disabled={!selected.length || competitorMutation.isPending}
                  onClick={() => competitorMutation.mutate(platform)}
                >
                  {competitorMutation.isPending ? (
                    <Loader2 className="spin" size={15} />
                  ) : (
                    <PackageSearch size={15} />
                  )}
                  读取WB竞品详情
                </Button>
              </div>
            </header>

            {platformResults.length > 0 && (
              <div className="compact-competitor-results">
                <section>
                  <header>
                    <strong>{platformLabels[platform]}</strong>
                    <span>
                      {platformItems.length
                        ? `${platformItems.length} 个有效样本`
                        : platformResults.some((result) => result.status === 'queued')
                          ? '正在读取'
                          : competitorFailureMessage(
                              platformResults.find((result) => result.status === 'failed')
                                ?.detail ?? emptyKeywordDetail(),
                            )}
                    </span>
                  </header>
                  {platformItems.length > 0 ? (
                    <div className="compact-product-links">
                      {platformItems.slice(0, 6).map((item) => (
                        <a
                          key={recordText(item.id) || recordText(item.link)}
                          href={recordText(item.link)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span>{recordText(item.title, '查看平台商品')}</span>
                          <small>
                            {typeof item.price === 'number'
                              ? `${item.price.toLocaleString()} ₽`
                              : '价格未公开'}
                            {typeof item.rating === 'number' ? ` · ${item.rating} 分` : ''}
                          </small>
                          <ExternalLink size={12} />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="compact-platform-fallback">
                      自动读取未返回可靠商品；上方平台搜索链接仍可使用。
                    </p>
                  )}
                </section>
              </div>
            )}
          </Card>
        </>
      )}

      <Card className="platform-verification-card" data-testid="platform-verification-card">
        <div className="platform-verification-copy">
          <div>
            <p className="eyebrow">PLATFORM VERIFICATION</p>
            <h3>去平台验证</h3>
          </div>
          <p>
            当前搜索词：<strong>{platformVerificationKeyword || '暂无'}</strong>
          </p>
        </div>
        <div className="platform-verification-actions">
          {platformVerificationKeyword ? (
            <>
              <a
                className="platform-verification-link is-wb"
                href={buildPlatformSearchUrl('wb', platformVerificationKeyword)}
                target="_blank"
                rel="noopener noreferrer"
              >
                去 WB 查看商品 <ExternalLink size={16} />
              </a>
              <a
                className="platform-verification-link is-ozon"
                href={buildPlatformSearchUrl('ozon', platformVerificationKeyword)}
                target="_blank"
                rel="noopener noreferrer"
              >
                去 Ozon 查看商品 <ExternalLink size={16} />
              </a>
            </>
          ) : (
            <>
              <button type="button" className="platform-verification-link is-wb" disabled>
                去 WB 查看商品 <ExternalLink size={16} />
              </button>
              <button type="button" className="platform-verification-link is-ozon" disabled>
                去 Ozon 查看商品 <ExternalLink size={16} />
              </button>
            </>
          )}
        </div>
      </Card>

      <div className="compact-source-footer">
        <Sparkles size={14} />
        AI 只用于翻译和相关性判断；搜索量、趋势和竞品数据不会由 AI 编造。
      </div>
    </section>
  );
}
